/**
 * WebCodecs 不可用时的 Opus WASM 上行编码器。
 * 输入/输出契约与 WebCodecs 实现一致：48 kHz 双声道 s16le → 48 kHz 单声道裸 Opus 包。
 */
import {
  OPUS_UPLINK_BITRATE_BPS,
  OPUS_UPLINK_FRAME_SAMPLES,
  OPUS_UPLINK_SAMPLE_RATE,
  getOpusUplinkInputGain,
} from '../../constants/Codec2Constants';

type EncodedPacketHandler = (opusPacket: ArrayBuffer) => void | Promise<void>;

interface WasmEncoderHandle {
  readonly frameSize: number;
  encodeFloat(pcm: Float32Array): Uint8Array;
  free(): void;
}

function stereoS16leToMonoF32(ab: ArrayBuffer, gain: number): Float32Array {
  if (ab.byteLength < 4) {
    return new Float32Array(0);
  }
  const frames = Math.floor(ab.byteLength / 4);
  const i16 = new Int16Array(ab, 0, frames * 2);
  const out = new Float32Array(frames);
  const scale = 1 / 32768;
  for (let i = 0; i < frames; i += 1) {
    const left = i16[i * 2];
    const right = i16[i * 2 + 1];
    out[i] = Math.max(-1, Math.min(1, (left + right) * 0.5 * scale * gain));
  }
  return out;
}

function copyPacketToArrayBuffer(packet: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(packet.byteLength);
  copy.set(packet);
  return copy.buffer;
}

export class OpusWasmUpstreamEncoder {
  private encoder: WasmEncoderHandle | null = null;
  private remainder = new Float32Array(0);
  private closed = false;

  constructor(private readonly onEncoded: EncodedPacketHandler) {}

  async ensureConfigured(): Promise<boolean> {
    if (this.closed) {
      return false;
    }
    if (this.encoder) {
      return true;
    }
    if (typeof WebAssembly === 'undefined') {
      console.warn('[WaveLink Opus uplink] 当前环境不支持 WebAssembly');
      return false;
    }

    try {
      // 仅在 WebCodecs 不可用时下载/实例化 WASM chunk，避免增加正常浏览器首屏负担。
      const libopus = await import('libopus-wasm');
      const encoder = await libopus.createEncoder({
        sampleRate: OPUS_UPLINK_SAMPLE_RATE,
        channels: 1,
        frameSize: OPUS_UPLINK_FRAME_SAMPLES,
        bitrate: OPUS_UPLINK_BITRATE_BPS,
        application: libopus.Application.Audio,
      });
      if (this.closed) {
        encoder.free();
        return false;
      }
      this.encoder = encoder;
      console.warn('[WaveLink Opus uplink] 已启用 WASM Opus 编码降级');
      return true;
    } catch (error) {
      console.error('[WaveLink Opus uplink] WASM Opus 初始化失败', error);
      this.encoder = null;
      return false;
    }
  }

  feedStereoS16le48k(audioData: ArrayBuffer): void {
    if (this.closed || !this.encoder) {
      return;
    }
    const mono = stereoS16leToMonoF32(audioData, getOpusUplinkInputGain());
    if (mono.length === 0) {
      return;
    }
    const merged = new Float32Array(this.remainder.length + mono.length);
    merged.set(this.remainder);
    merged.set(mono, this.remainder.length);
    this.remainder = merged;

    while (this.remainder.length >= OPUS_UPLINK_FRAME_SAMPLES) {
      this.encodeOneFrame(this.remainder.subarray(0, OPUS_UPLINK_FRAME_SAMPLES));
      this.remainder = this.remainder.slice(OPUS_UPLINK_FRAME_SAMPLES);
    }
  }

  private encodeOneFrame(frame: Float32Array): void {
    if (!this.encoder || this.closed) {
      return;
    }
    try {
      const packet = this.encoder.encodeFloat(new Float32Array(frame));
      void Promise.resolve(this.onEncoded(copyPacketToArrayBuffer(packet))).catch((error) => {
        console.error('[WaveLink Opus uplink] WASM output 回调失败', error);
      });
    } catch (error) {
      console.error('[WaveLink Opus uplink] WASM 编码失败', error);
    }
  }

  async flush(): Promise<void> {
    if (this.closed || !this.encoder) {
      this.remainder = new Float32Array(0);
      return;
    }
    if (this.remainder.length > 0) {
      const padded = new Float32Array(OPUS_UPLINK_FRAME_SAMPLES);
      padded.set(this.remainder.subarray(0, OPUS_UPLINK_FRAME_SAMPLES));
      this.remainder = new Float32Array(0);
      this.encodeOneFrame(padded);
    }
    // WASM encodeFloat 同步输出，不需要额外 flush；保留 async 接口与 WebCodecs 对齐。
    await Promise.resolve();
  }

  close(): void {
    this.closed = true;
    try {
      this.encoder?.free();
    } catch (error) {
      console.warn('[WaveLink Opus uplink] 释放 WASM 编码器失败', error);
    }
    this.encoder = null;
    this.remainder = new Float32Array(0);
  }

  isConfigured(): boolean {
    return this.encoder !== null && !this.closed;
  }
}
